<template>
  <div v-bind:class="isInNewMode ? 'personal-details':''">
    <section class="section">
      <b-loading :active="isLoading"></b-loading>
      <div class="container">
        <div class="columns">
          <div class="column is-full">
            <div class="box">
              <div class="columns" style="margin-top: 16px">
                <div class="column">
                  <header class="heading">
                    <p class="modal-card-title">{{ pageTitle }}</p>
                  </header>
                </div>
                <div v-if="!isEditMode" class="column is-narrow">
                  <b-button class="button" @click="backClicked" icon-left="arrow-left-bold" tabindex="1">
                    {{ $t('common.back') }}
                  </b-button>
                </div>
                <div v-if="!isEditMode && ((!this.is_template && currentUserAccess('PersonalDetails', this.UserActions.UPDATE, this.project_id)) 
                                          || (this.is_template && currentUserAccess('ProjectTemplates', this.UserActions.UPDATE)))"
                     class="column is-narrow">
                  <b-button class="button is-primary" @click="editClicked" icon-left="pencil" tabindex="2">
                    {{ $t('common.edit') }}
                  </b-button>
                </div>
                <div
                    v-if="!isEditMode && ((!this.is_template && currentUserAccess('PersonalDetails', this.UserActions.DELETE, this.project_id)) 
                                          || (this.is_template && currentUserAccess('ProjectTemplates', this.UserActions.DELETE)))"
                    class="column is-narrow">
                  <b-button class="button is-primary" type="is-danger" @click="deleteClicked" icon-left="delete"
                            tabindex="3">
                    {{ $t('common.delete') }}
                  </b-button>
                </div>
              </div>
              <div class="columns" style="margin-top: 16px">
                <div class="column">
                  <div class="columns">
                    <div class="column"></div>
                    <div class="column is-narrow">
                      <b-field>
                        <b-button v-if="isEditMode && isInNewMode" icon-left="account-hard-hat" class="button is-light" @click="switchUserInput" tabindex="9">
                          {{ userInputModeButtonTitle }}
                        </b-button>
                      </b-field>
                    </div>
                  </div>
                  <b-field v-if="isEditMode && isSelectUserListMode"
                           :label="$t('personal-details-view.select-user-field')">
                    <b-taginput
                        ref="userInput"
                        v-if="isEditMode"
                        maxtags="1"
                        v-model="selectedUsers"
                        :data="filteredUsers"
                        field="fullName"
                        :loading="isFetchingUsers"
                        :has-counter="false"
                        :open-on-focus="true"
                        :allow-new="false"
                        autocomplete
                        :remove-on-keys=[]
                        :confirm-keys=[]
                        :placeholder="$t('personal-details-view.select-user-placeholder')"
                        tabindex="10"
                        @typing="getFilteredUsers"
                        @input="onUsersInput"
                        @blur="onUserInputBlur"
                        @add="onUserSelected"
                        @remove="onUserRemoved">
                    </b-taginput>
                  </b-field>
                  <b-field v-if="isEditMode && !isSelectUserListMode"
                           :label="$t('personal-details-view.personal-firstname-field')">
                    <BInputWithValidation rules="required" v-model="personalData.name.first"
                                          :placeholder="$t('personal-details-view.personal-firstname-placeholder')"
                                          :readonly="isUserNameReadOnly"
                                          tabindex="11"></BInputWithValidation>
                  </b-field>
                  <b-field v-if="isEditMode && !isSelectUserListMode"
                           :label="$t('personal-details-view.personal-lastname-field')">
                    <BInputWithValidation rules="required" v-model="personalData.name.last"
                                          :placeholder="$t('personal-details-view.personal-lastname-placeholder')"
                                          :readonly="isUserNameReadOnly"
                                          tabindex="12"></BInputWithValidation>
                  </b-field>
                  <b-field :label="$t('personal-details-view.personal-group-field')">
                    <div v-if="isEditMode" class="columns">
                      <div style="margin: 12px;"
                           v-for="option in personalGroupOptions"
                           :value="option.id"
                           :key="option.id">
                        <b-radio v-model="personalData.group" :native-value="option.id">
                          {{ $t(option.translationKey) }}
                        </b-radio>
                      </div>
                    </div>
                    <label v-else>{{ personalGroup }}</label>
                  </b-field>
                  <b-field :label="$t('personal-details-view.personal-agency-field')">
                    <b-taginput
                        ref="agencyInput"
                        v-if="isEditMode"
                        maxtags="1"
                        v-model="selectedAgencies"
                        :data="filteredAgencies"
                        :loading="isFetchingCategories"
                        :has-counter="false"
                        :open-on-focus="true"
                        :allow-new="true"
                        autocomplete
                        :remove-on-keys=[]
                        :placeholder="$t('personal-details-view.personal-agency-placeholder')"
                        tabindex="14"
                        @typing="getFilteredAgencies"
                        @input="onAgenciesInput"
                        @blur="onAgencyInputBlur">
                    </b-taginput>
                    <b-taglist v-else>
                      <b-tag type="is-info is-light" size="is-medium">
                        {{ selectedAgencies[0] }}
                      </b-tag>
                    </b-taglist>
                  </b-field>
                  <b-field :label="$t('personal-details-view.personal-category-field')">
                    <b-taginput
                        ref="categoryInput"
                        v-if="isEditMode"
                        maxtags="1"
                        v-model="selectedCategories"
                        :data="filteredCategories"
                        :loading="isFetchingCategories"
                        :has-counter="false"
                        :open-on-focus="true"
                        :allow-new="false"
                        autocomplete
                        :remove-on-keys=[]
                        :confirm-keys=[]
                        :placeholder="$t('personal-details-view.personal-category-placeholder')"
                        tabindex="15"
                        @typing="getFilteredCategories"
                        @input="onCategoriesInput"
                        @blur="onCategoryInputBlur">
                    </b-taginput>
                    <b-taglist v-else>
                      <b-tag type="is-info is-light" size="is-medium">
                        {{ selectedCategories[0] }}
                      </b-tag>
                    </b-taglist>
                  </b-field>
                  <b-field :label="$t('personal-details-view.personal-text-field')">
                    <b-input v-if="isEditMode" v-model="personalData.text"
                             :placeholder="$t('personal-details-view.personal-text-placeholder')"
                             type="textarea" tabindex="16"></b-input>
                    <label v-else style="white-space: pre-wrap;">{{ personalData.text }}</label>
                  </b-field>
                </div>
              </div>
              <div class="columns is-mobile" style="margin-top: 16px" v-if="isEditMode">
                <div class="column is-narrow">
                  <b-button class="button" @click="cancelClicked" icon-left="close-thick" tabindex="31">
                    {{ $t('common.cancel') }}
                  </b-button>
                </div>
                <div class="column is-narrow">
                  <b-button v-if="((!this.is_template && currentUserAccess('PersonalDetails', this.UserActions.UPDATE, this.project_id))
                                                  || (this.is_template && currentUserAccess('ProjectTemplates', this.UserActions.UPDATE)))"
                            class="button is-primary" @click="saveClicked" icon-left="content-save"
                            tabindex="32">
                    {{ $t('common.save') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import firebase from "firebase/app";
import BInputWithValidation from "./inputs/BInputWithValidation";

export default {
  name: "PersonalDetails",
  components: {
    BInputWithValidation
  },
  props: {
    'project_id': {
      type: String,
    },
    'personal_id': {
      type: String,
    },
    'is_template': {
      type: Boolean,
    }
  },
  data() {
    return {
      personalData: {
        name: {}
      },
      isLoading: false,
      isEditMode: false,
      isSelectUserListMode: false,
      isUserNameReadOnly: false,
      isFetchingCategories: false,
      isFetchingUsers: false,
      categoricalData: {},
      filteredCategories: [],
      selectedCategories: [],
      filteredAgencies: [],
      filteredUsers: [],
      selectedAgencies: [],
      selectedUsers: [],
      usersData: [],
      userInputModeButtonTitle: this.$t('personal-details-view.select-user-placeholder')
    }
  },
  methods: {
    readPersonalData: function () {
      this.isLoading = true;
      const functions = firebase.app().functions("europe-west3");
      const getPersonalFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getPersonalFunction({
        action: "get_personal_by_id",
        project_id: this.project_id,
        document_id: this.personal_id,
        fill_meta: false
      }).then((result) => {
        this.isLoading = false;
        console.log("read personal result: ", result.data);
        this.personalData = result.data["personnel"];
        const agency = this.personalData.agency
        if (agency !== undefined && agency !== "") {
          this.selectedAgencies[0] = agency
        }
        const category = this.personalData.category
        if (category !== undefined && category !== "") {
          this.selectedCategories[0] = category
        }
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting personnel: ", error);
      });
    },
    createPersonal: function (updateData) {
      this.isLoading = true
      const functions = firebase.app().functions("europe-west3");
      const insertPersonalFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "insert_personal";
      console.log(updateData);
      insertPersonalFunction(updateData).then((result) => {
        this.isLoading = false
        console.log("create personal result: ", result.data);
        this.showSuccess(this.$t('messages.personal-created'));
        this.$emit('personalAdded');
      }).catch((error) => {
        this.isLoading = false
        this.showError(error.message)
        this.showError("Error writing document: " + error);
      });
    },
    updatePersonal: function (updateData) {
      this.isLoading = true;
      console.log(updateData);
      const functions = firebase.app().functions("europe-west3");
      const updatePersonalFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "update_personal";
      updatePersonalFunction(updateData).then((result) => {
        this.isLoading = false;
        this.isEditMode = false;
        console.log("update result: ", result.data);
        this.showSuccess(this.$t('messages.personal-updated'));
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting personal: ", error);
      });
    },
    deletePersonal: function () {
      this.isLoading = true;
      console.log(this.personalData);
      const functions = firebase.app().functions("europe-west3");
      const deletePersonalFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deletePersonalFunction({
        action: "delete_personal",
        project_id: this.project_id,
        document_id: this.personal_id,
      }).then((result) => {
        this.isLoading = false;
        this.isEditMode = false;
        console.log("delete personal result: ", result.data);
        this.showSuccess(this.$t('messages.personal-deleted'));
        this.$emit('personalDeleted');
        this.$router.back();
      }).catch((error) => {
        this.showError(error.message)
        this.isLoading = false;
        console.log("Error getting personal: ", error);
      });
    },
    readCategoricalData: function () {
      this.isFetchingCategories = true
      const functions = firebase.app().functions("europe-west3");
      const getCategoriesFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getCategoriesFunction({
        action: "get_categories",
        collections: ["personnel"]
      }).then((result) => {
        this.isFetchingCategories = false
        console.log("read categories result: ", result.data);
        this.categoricalData = result.data["personnel"]
        this.filteredCategories = this.categoricalData.category
        this.filteredAgencies = this.categoricalData.agency
      }).catch((error) => {
        this.isFetchingCategories = false
        this.showError(error.message)
        console.log("Error getting categories: ", error);
      });
    },
    getUsers: function () {
      this.isFetchingUsers = true
      const functions = firebase.app().functions("europe-west3");
      const getUsersFunction = functions.httpsCallable(this.getSubmitEndpoint());
      const payload = {
        action: "get_users", fill_meta: true,
        filters: [['is_archived', '==', false]]
      }
      payload.filters.push(['roles', 'array_contains_any', ['admin', 'project_manager', 'site_manager']])
      getUsersFunction(payload)
          .then((result) => {
            console.log("result: ", result.data);
            result.data["users"].forEach((user) => {
              this.usersData.push({
                user: user,
                fullName: this.getFullName(user.name),
                firstName: user.name.first,
                lastName: user.name.last,
                role: user.roles[0]
              });
            });
            console.log("users data: ", this.usersData);
            this.filteredUsers = this.usersData
            this.isFetchingUsers = false
          })
          .catch((error) => {
            console.log("Error getting users: ", error);
            this.showError(error.message)
            this.isFetchingUsers = false
          });
    },
    /*
     UI Events
     */
    editClicked: function () {
      this.isEditMode = !this.isEditMode;
    },
    deleteClicked: function () {
      this.$buefy.dialog.confirm({
        title: this.$t('personal-details-view.delete-personal-confirm-title').toString(),
        message: this.$t('personal-details-view.delete-personal-confirm-message').toString(),
        confirmText: this.$t('personal-details-view.delete-personal-confirm-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        hasIcon: true,
        icon: 'delete',
        onConfirm: () => {
          this.deletePersonal();
        }
      });
    },
    cancelClicked: function () {
      if (this.isInNewMode) {
        this.$emit('close');
      } else if (this.isEditMode) {
        this.readPersonalData();
        this.isEditMode = false;
      }
    },
    backClicked: function () {
      this.$router.back();
    },
    saveClicked: function () {
      const dataFieldsToUpdate = {
        project_id: this.project_id,
        name: this.personalData.name,
        group: this.personalData.group,
        text: this.personalData.text
      }
      if (this.selectedCategories[0] !== undefined) {
        dataFieldsToUpdate.category = this.selectedCategories[0]
      }
      if (this.selectedAgencies[0] !== undefined) {
        dataFieldsToUpdate.agency = this.selectedAgencies[0]
      }
      if (this.isEmptyOrNull(dataFieldsToUpdate.name) ||
          this.isEmptyOrNull(dataFieldsToUpdate.group) ||
          this.isEmptyOrNull(dataFieldsToUpdate.category) ||
          this.isEmptyOrNull(dataFieldsToUpdate.agency)) {
        this.showError(this.$t('messages.empty-form-error').toString())
        return
      }
      if (this.isInNewMode) {
        this.createPersonal(dataFieldsToUpdate);
      } else {
        dataFieldsToUpdate.document_id = this.personal_id;
        this.updatePersonal(dataFieldsToUpdate);
      }
    },
    onCategoriesInput() {
      this.filteredCategories = this.categoricalData.category
    },
    getFilteredCategories(text) {
      this.filteredCategories = this.categoricalData.category.filter((option) => {
        return option
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    onAgenciesInput() {
      this.filteredAgencies = this.categoricalData.agency
    },
    getFilteredAgencies(text) {
      this.filteredAgencies = this.categoricalData.agency.filter((option) => {
        return option
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    onUsersInput() {
      this.filteredUsers = this.usersData
    },
    getFilteredUsers(text) {
      this.filteredUsers = this.usersData.filter((option) => {
        return option.fullName.toLowerCase().indexOf(text.toLowerCase()) >= 0
      })
    },
    onCategoryInputBlur() {
      this.$refs.categoryInput.newTag = '';
    },
    onAgencyInputBlur() {
      this.$refs.agencyInput.addTag()
    },
    onUserInputBlur() {
      this.$refs.userInput.newTag = '';
    },
    onUserSelected(user) {
      console.log("USER ADDED: " + user.fullName)
      this.personalData.name.first = user.firstName
      this.personalData.name.last = user.lastName
      this.isUserNameReadOnly = true
    },
    onUserRemoved(user) {
      console.log("USER REMOVED: " + user.fullName)
      this.personalData.name.first = ''
      this.personalData.name.last = ''
      this.isUserNameReadOnly = false
    },
    switchUserInput() {
      if (this.isSelectUserListMode) {
        this.$refs.userInput.removeLastTag()
        this.userInputModeButtonTitle = this.$t('personal-details-view.select-user-from-list');
      } else {
        this.personalData.name.first = ''
        this.personalData.name.last = ''
        this.userInputModeButtonTitle = this.$t('personal-details-view.select-custom-user');
      }
      this.isSelectUserListMode = !this.isSelectUserListMode
    }
  },
  mounted() {
    if ((!this.is_template && !this.currentUserAccess("PersonalDetails", this.UserActions.VIEW, this.project_id))
        || (this.is_template && !this.currentUserAccess("ProjectTemplates", this.UserActions.VIEW))) {
      console.log("Page access denied.");
      this.$router.replace("/projects/").catch(() => {
      });
    }
    this.getUsers()
    this.readCategoricalData()
    if (this.isInNewMode) {
      console.log("creating new personal");
      this.isEditMode = true;
    } else {
      console.log("edit personal : ", this.personal_id);
      this.isEditMode = false;
      this.readPersonalData();
    }
  },
  computed: {
    isInNewMode() {
      return this.personal_id === "new";
    },
    pageTitle() {
      if (this.isInNewMode) {
        return this.$t('personal-details-view.new-personal-title')
      } else if (this.isEditMode) {
        return this.$t('personal-details-view.edit-personal-title') + " " + this.getAdjustedTitle(this.getFullName(this.personalData.name))
      } else {
        return this.$t('personal-details-view.view-personal-title') + " " + this.getAdjustedTitle(this.getFullName(this.personalData.name))
      }
    },
    personalGroup() {
      if (!this.isEditMode) {
        const st = this.personalGroupOptions.find(obj => {
          return obj.id === this.personalData.group
        });
        if (st !== undefined)
          return this.$t(st.translationKey);
      }
      return "";
    },
  }
};
</script>
